// ============================================================
// useFiringSolutions - debounced per-zone outcome math for the
// firing solutions board (v8.250.0). Mirror of useProbability:
// same inputs, same debounce, same "the plot must match the roll"
// rule - plus the called-shot state, because the live attackMod
// FOLDS IN an engaged called-shot chip and the board must back it
// out before applying the penalty per row (see
// computeFiringSolutions).
// ============================================================
import { useMemo } from 'react';
import { computeFiringSolutions } from '../engine/probability';
import type { UnnaturalDosMode } from '../engine/unnaturalDos';
import type { ProbabilityInput, FiringSolutions } from '../engine/probability';
import type { Slot, Target } from '../types';
import type { AttackerCombatStats } from '../engine/character/types';
import type { ConditionModOptions } from '../engine/conditionModifiers';
import { useDebouncedResult } from './useDebouncedResult';
import { attackProbabilityInput } from './useProbability';

/** Lighter than the forecast's 1000: this runs once per zone. */
const ZONE_ITERATIONS = 400;

interface UseFiringSolutionsParams {
  slot: Slot | null;
  target: Target | null;
  /** The LIVE modifier - including an engaged called-shot chip. */
  attackMod: number;
  /** Whether that chip is currently folded into attackMod. */
  calledShot: boolean;
  calledShotReduction: number;
  /** The attack is a charge (v8.368.2). */
  charge?: boolean;
  rangeBand: string;
  fireMode: string;
  isMelee: boolean;
  hatred: boolean;
  frenzied: boolean;
  attackerCharStats: AttackerCombatStats | null;
  unnaturalDosMode?: UnnaturalDosMode;
  attackerConditions?: { conditions: string[] } & Record<string, unknown>;
  applyConditionMods?: boolean;
  condOptions?: ConditionModOptions;
  aimed?: boolean;
  /** The re-roll damage pill (v8.390.3) - every row is the live attack, so it re-rolls too. */
  rerollDamage?: boolean;
}

interface UseFiringSolutionsReturn {
  result: FiringSolutions | null;
  loading: boolean;
}

/**
 * The called-shot penalty after the talent. Same clamp as the targeting
 * console and calcSkillBreakdown: the talent reduces the penalty, never
 * inverts it.
 */
const calledShotPenalty = (reduction: number) => Math.max(20 - Math.min(Math.max(reduction || 0, 0), 20), 0);

interface ZoneRun {
  input: ProbabilityInput;
  penalty: number;
}

const computeZones = ({ input, penalty }: ZoneRun) => computeFiringSolutions(input, penalty, ZONE_ITERATIONS);

/**
 * The board's run: the live attack with an engaged chip BACKED OUT -
 * computeFiringSolutions applies the penalty itself, per row, and must not
 * count it twice.
 */
function zoneRun(p: UseFiringSolutionsParams & { slot: Slot; target: Target }): ZoneRun {
  const penalty = calledShotPenalty(p.calledShotReduction);
  const input = attackProbabilityInput({ ...p, attackMod: p.attackMod + (p.calledShot ? penalty : 0) });
  return { input, penalty };
}

export default function useFiringSolutions(params: UseFiringSolutionsParams): UseFiringSolutionsReturn {
  const { slot, target, attackMod, calledShot, calledShotReduction, charge, rangeBand, fireMode, isMelee } = params;
  const { hatred, frenzied, attackerCharStats, unnaturalDosMode, attackerConditions, applyConditionMods } = params;
  const { condOptions, aimed, rerollDamage } = params;
  // Long only because the run has this many fields: the memo lists what it reads.
  const run = useMemo(
    () =>
      slot && !slot.isEmpty && target
        ? zoneRun({
            slot,
            target,
            attackMod,
            calledShot,
            calledShotReduction,
            charge,
            rangeBand,
            fireMode,
            isMelee,
            hatred,
            frenzied,
            attackerCharStats,
            unnaturalDosMode,
            attackerConditions,
            applyConditionMods,
            condOptions,
            aimed,
            rerollDamage,
          })
        : null,
    [
      slot,
      target,
      attackMod,
      calledShot,
      calledShotReduction,
      charge,
      rangeBand,
      fireMode,
      isMelee,
      hatred,
      frenzied,
      attackerCharStats,
      unnaturalDosMode,
      attackerConditions,
      applyConditionMods,
      condOptions,
      aimed,
      rerollDamage,
    ],
  );
  return useDebouncedResult(run, computeZones);
}
